import React from 'react';
import { FormState } from '../../hooks/useFormState.types';
import Box from '@material-ui/core/Box';
import { RadioGroupProps } from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';

import { useTranslation } from '../../hooks/useTranslation';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SelectableInput from './SelectableInput';
import { useMakeSelectProps } from './EitherField.hooks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    radioInputs: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
      '& .MuiTextField-root': {
        margin: theme.spacing(1),
        flexDirection: 'column',
        display: 'flex',
      },
    },
    or: {
      marginLeft: theme.spacing(3),
      marginRight: theme.spacing(3),
      marginTop: '1.6em',
      fontWeight: 'normal',
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        marginRight: 0,
        marginTop: '0.5em',
        marginBottom: '0.5em',
        width: '100%',
        textAlign: 'center',
      },
    },
  }),
);

type Props<Model extends Record<string, any>> = {
  noDivider?: boolean;
  form: FormState<Model>;
  selectorField: keyof Model;
  inputFields: [keyof Model, keyof Model];

  renderLeft: React.ReactElement;
  leftHelper?: React.ReactNode;

  renderRight: React.ReactElement;
  rightHelper?: React.ReactNode;
} & RadioGroupProps;

const Divider = (props: { className: string; label: string }) => (
  <Box className={props.className} flexGrow={0}>
    <Typography variant={'body1'}>{props.label}</Typography>
  </Box>
);

// Handles selecting between 2 inputs with proper UX
// The radio buttons are using the MUI Radio component to get the right styling,
// and with state management, they act like radio buttons, but they are actually
// checkboxes.  This is so that they each can be tabbed to as the user tabs through
// the page.
export default function EitherField<Model extends Record<string, any>>({
  form,
  selectorField,
  inputFields: [leftField, rightField],
  renderLeft,
  renderRight,
  leftHelper,
  rightHelper,
  noDivider = false,
}: Props<Model>) {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    makeProps,
    refs: [leftRef, rightRef],
  } = useMakeSelectProps(form, selectorField, [leftField, rightField]);

  const left = React.cloneElement(renderLeft, {
    ...makeProps(leftField, renderLeft.props),
    ref: leftRef,
  });

  const right = React.cloneElement(renderRight, {
    ...makeProps(rightField, renderRight.props),
    ref: rightRef,
  });

  return (
    <Box className={classes.radioInputs}>
      <SelectableInput
        fieldName={leftField}
        form={form}
        field={left}
        selectorField={selectorField}
        inputRef={leftRef}
        helper={leftHelper}
        radioTestId="left-radio"
      />
      {noDivider === false && (
        <Divider className={classes.or} label={t('OR')} />
      )}

      <SelectableInput
        fieldName={rightField}
        form={form}
        field={right}
        selectorField={selectorField}
        inputRef={rightRef}
        helper={rightHelper}
        radioTestId="right-radio"
      />
    </Box>
  );
}
